// Kurzlebiger In-Memory-Cache fuer GET /api/**-Antworten (SWR) samt der
// Entscheidung, welche Aufrufe ueberhaupt hinein duerfen.
//
// Lag bis #309 inline in apiService.js. Herausgeloest, weil der Cache dort
// weder gedeckelt noch pruefbar war: Die Datei ueberschreibt beim Import
// global axios.get, ein Test haette diesen Seiteneffekt mitgenommen.
// Die Entscheidungslogik selbst ist frei von Abhaengigkeiten und damit
// direkt pruefbar.

export const SWR_CACHE_TTL_MS_DEFAULT = 5000; // 5s

// #309: Obergrenze. Der Cache haelt vollstaendige Axios-Antworten; ohne
// Deckel und ohne Aufraeumung blieb jeder Eintrag bis zum Schliessen des
// Tabs liegen. Fuer die PWA am Handy, wo Tabs tagelang offen bleiben, war
// das monotoner Zuwachs. 100 Eintraege decken jede realistische Burst-Last
// ab -- die TTL betraegt 5 Sekunden, es geht nur um Gleichzeitigkeit.
export const SWR_CACHE_MAX_ENTRIES = 100;

const swrCache = new Map(); // key -> { expiry:number, response:AxiosResponse }

/**
 * Erkennt einen Cache-Buster (`?_=<Zeitstempel>` oder `params._`).
 *
 * #110 hat den Buster eingefuehrt, damit der BROWSER die Antwort nicht
 * heuristisch zwischenspeichert -- neu angelegte Kunden erschienen sonst
 * erst nach einem Reload im Auswahlmenue. Fuer den SWR-Cache war er
 * allerdings ein Eigentor: Der Zeitstempel geht in den Schluessel ein, also
 * entstand bei JEDEM Aufruf ein neuer Eintrag, der nie wieder getroffen
 * werden konnte. Gespeichert und sofort unauffindbar -- reiner Muellzuwachs.
 */
export const hasCacheBuster = (url, config) => {
  try {
    if (typeof url === 'string' && /[?&]_=/.test(url)) return true;
    const params = config && config.params;
    if (params && Object.prototype.hasOwnProperty.call(params, '_')) return true;
    return false;
  } catch (_) {
    return false;
  }
};

export const isApiGetEligibleForCache = (url, config) => {
  try {
    if (config && config.disableSWR) return false;
    if (typeof url !== 'string') return false;
    if (!url.startsWith('/api/')) return false;
    // #309: Ein Cache-Buster ist die ausdrueckliche Bitte um eine frische
    // Antwort. So ein Aufruf darf den Cache weder lesen noch fuellen --
    // nicht nur wegen des Muellzuwachses, sondern weil alles andere der
    // Absicht des Aufrufers widerspraeche.
    if (hasCacheBuster(url, config)) return false;
    // Never cache admin endpoints or notification config
    if (url.startsWith('/api/admin')) return false; // admin pages must be fresh
    if (url.startsWith('/api/notification')) return false; // global notice must be fresh
    // Avoid caching health checks by default
    if (url.startsWith('/api/health')) return false;
    // Do not cache endpoints that must reflect immediate changes
    if (url.endsWith('/history') || url.includes('/history')) return false; // ticket history
    if (url.startsWith('/api/tickets/planned')) return false; // planned tickets for calendar
    // Tickets:
    // - allow a VERY short SWR cache ONLY for the tickets list endpoint to coalesce bursts
    // - keep detail/mutation-ish endpoints uncached to stay "instant"
    if (url.startsWith('/api/tickets')) {
      const basePath = url.split('?')[0];
      if (basePath === '/api/tickets') return true; // list endpoint (incl. trailing '?' and query params)
      return false;
    }
    if (url.startsWith('/api/projects') || url.startsWith('/api/subprojects')) return false; // project views
    // Calendar views must be live to reflect drag/drop/resize/unplan immediately
    if (url.startsWith('/api/events')) return false; // personal calendar events
    if (url.startsWith('/api/global-events')) return false; // multi-user calendar events
    // Emails sollen immer live sein (SSE triggert sofortigen Refresh)
    if (url.startsWith('/api/emails')) return false;
    return true;
  } catch (_) {
    return false;
  }
};

export const buildCacheKey = (url, config) => {
  // Most calls already include query in url; include method and serialized params for safety
  const params = config && config.params ? JSON.stringify(config.params) : '';
  return `GET ${url} || ${params}`;
};

/**
 * Liefert die zwischengespeicherte Antwort, solange sie frisch ist.
 * Abgelaufene Eintraege werden dabei gleich entfernt.
 */
export const readSwrCache = (key, now = Date.now()) => {
  const cached = swrCache.get(key);
  if (!cached) return undefined;
  if (cached.expiry > now) return cached.response;
  swrCache.delete(key);
  return undefined;
};

/**
 * Legt eine Antwort ab und haelt den Cache unter SWR_CACHE_MAX_ENTRIES.
 * Zuerst fliegen abgelaufene Eintraege raus, erst danach die aeltesten --
 * eine Map iteriert in Einfuegereihenfolge, und aus ihr zu loeschen ist
 * waehrend der Iteration zulaessig.
 */
export const writeSwrCache = (key, response, ttlMs, now = Date.now()) => {
  swrCache.delete(key); // neu einfuegen = ans Ende, damit die Reihenfolge stimmt
  swrCache.set(key, { expiry: now + ttlMs, response });
  if (swrCache.size <= SWR_CACHE_MAX_ENTRIES) return;

  for (const [k, entry] of swrCache) {
    if (swrCache.size <= SWR_CACHE_MAX_ENTRIES) break;
    if (entry.expiry <= now) swrCache.delete(k);
  }
  while (swrCache.size > SWR_CACHE_MAX_ENTRIES) {
    const oldest = swrCache.keys().next().value;
    if (oldest === undefined) break;
    swrCache.delete(oldest);
  }
};

// Nur fuer Tests und Diagnose.
export const swrCacheSize = () => swrCache.size;
export const resetSwrCache = () => swrCache.clear();
